import { Conversation, Message, MessagePartner, MessagePartnerHash, MessageType } from "../types";
import { _ } from "libs";

export class ChatConversation {
	conversationId: string;
	numberOfUnreadMessages: number;
	messages: Message[];
	hasMore: boolean;
	partners: MessagePartner[];
	partnersHash: MessagePartnerHash;
	lastUpdate: Date;
	typing: string[];

	constructor(
		conversationId: string,
		numberOfUnreadMessages: number,
		messages: Message[],
		hasMore: boolean,
		partners: MessagePartner[],
		partnersHash: MessagePartnerHash,
		lastUpdate: Date
	) {
		this.conversationId = conversationId;
		this.numberOfUnreadMessages = numberOfUnreadMessages;
		this.messages = messages;
		this.hasMore = hasMore;
		this.partners = partners;
		this.partnersHash = partnersHash;
		this.lastUpdate = lastUpdate;
	}

	addMessage = (message: Message, increaseUnreadCount: boolean): void => {
		this.messages = [message].concat(this.messages);
		this.lastUpdate = new Date(message.updatedAt);

		if (increaseUnreadCount) {
			this.numberOfUnreadMessages = this.numberOfUnreadMessages + 1;
		}
	};

	addRead = (messageId: string, readerId: string): void => {
		for (let i in this.messages) {
			const message = this.messages[i];
			if (message.messageId === messageId) {
				if (message.readBy.indexOf(readerId) === -1) {
					message.readBy = message.readBy.concat(readerId);
					this.messages[i] = { ...message };
					break;
				}
			}
		}

		this.numberOfUnreadMessages = Math.max(0, this.numberOfUnreadMessages - 1);
		this.lastUpdate = new Date();
	};

	deleteMessage = (messageId: string): void => {
		for (let i in this.messages) {
			const message = this.messages[i];
			if (message.messageId === messageId) {
				message.text.payload.messagePayload.message = _("message.deleted");
				message.text.deleted = true;
				this.messages[i] = { ...message };
				break;
			}
		}

		this.numberOfUnreadMessages = Math.max(0, this.numberOfUnreadMessages - 1);
		this.lastUpdate = new Date();
	};

	removeMessage = (messageId: string): void => {
		for (let i in this.messages) {
			const message = this.messages[i];
			if (message.messageId === messageId) {
				delete this.messages[i];
				break;
			}
		}

		this.numberOfUnreadMessages = Math.max(0, this.numberOfUnreadMessages - 1);
		this.lastUpdate = new Date();
	};

	addOlderMessages = (messages: Message[]): void => {
		this.messages = this.messages.concat(messages);
		this.hasMore = messages.length === 20;
	};

	getCopy = (): ChatConversation => {
		return new ChatConversation(
			this.conversationId,
			this.numberOfUnreadMessages,
			this.messages,
			this.hasMore,
			this.partners,
			this.partnersHash,
			this.lastUpdate
		);
	};

	markAllRead = (): void => {
		this.numberOfUnreadMessages = 0;
	};
}

export type ConversationCallback = (conversation: ChatConversation, key: number) => void;

type ConversationTuple = [string, ChatConversation];

export type ChatConversationListData = {
	conversationId: string;
	numberOfUnreadMessages: number;
	partners: MessagePartner[];
	lastUpdate: Date;
	messagePreview: string;
	lastSenderId: string;
	partnersHash: MessagePartnerHash;
};

export default class ConversationsMap {
	dataMap = {} as { [key: string]: ChatConversation };
	hasMore = false;
	updatedAt = null as number;

	constructor(map = {}, hasMore = false) {
		this.dataMap = map;
		this.hasMore = hasMore;
	}

	markUpdate = () => {
		this.updatedAt = Date.now();
	};

	static fromResponse = (response: Conversation[]): ConversationsMap => {
		const result = new ConversationsMap();

		response.forEach((conversation) => {
			result.add(conversation);
		});

		return result;
	};

	static convertConversation = (conversation: Conversation): ChatConversation => {
		if (conversation === undefined) {
			return null;
		}

		if (conversation.lastMessages[0] === undefined) {
			return null;
		}

		const lastUpdate = new Date(conversation.lastMessages[0].updatedAt);
		return new ChatConversation(
			conversation.conversationId,
			conversation.numberOfUnreadMessages || 0,
			[...conversation.lastMessages],
			conversation.lastMessages.length === 20,
			conversation.partners,
			ConversationsMap.createPartnersHash(conversation.partners),
			lastUpdate
		);
	};

	static createPartnersHash = (partners) => {
		const result = {};

		partners.forEach((partner) => {
			result[partner.userId] = partner;
		});

		return result;
	};

	count = (): number => {
		return Object.entries(this.dataMap).length;
	};

	add = (conversationData: Conversation): ChatConversation | null => {
		const conversation = ConversationsMap.convertConversation(conversationData);

		if (!conversation) {
			return null;
		}

		this.dataMap[conversation.conversationId] = conversation;
		this.markUpdate();
		return conversation;
	};

	get = (id: string): ChatConversation | null => {
		if (this.dataMap.hasOwnProperty(id)) {
			return { ...this.dataMap[id] };
		}

		return null;
	};

	has = (id: string): boolean => {
		if (this.dataMap.hasOwnProperty(id)) {
			return true;
		}

		return false;
	};

	forEach = (callback: ConversationCallback): void => {
		this.sort().forEach(({ id }, key) => {
			const conversation = this.dataMap[id];
			callback(conversation, key);
		});
	};

	map = (callback: ConversationCallback): any[] => {
		const result = [];
		this.sort().forEach(({ id }, key) => {
			const conversation = this.dataMap[id];
			result.push(callback(conversation, key));
		});
		return result;
	};

	getLast = (): ChatConversation | null => {
		const sorted = this.sort();
		if (sorted.length > 0) {
			return this.dataMap[sorted[0]["id"]];
		}
		return null;
	};

	getOldest = (): ChatConversation | null => {
		const sorted = this.sort();
		if (sorted.length > 0) {
			return this.dataMap[sorted[sorted.length - 1]["id"]];
		}
		return null;
	};

	getOldestMessage = (): Message | null => {
		const oldestConversation = this.getOldest();
		if (oldestConversation) {
			return oldestConversation.messages[oldestConversation.messages.length - 1];
		}

		return null;
	};

	getCopy = (): ConversationsMap => {
		return new ConversationsMap({ ...this.dataMap }, this.hasMore);
	};

	findLastMessage = (conversation: ChatConversation): Message | undefined => {
		for (let message of conversation.messages) {
			if (Object.values(MessageType).includes(message.text?.messageType)) {
				return message;
			}
		}
	};

	getListData = (): Array<ChatConversationListData> => {
		return this.map((conversation) => {
			let messagePreview = "";
			let lastSenderId = null;
			const lastMessage = this.findLastMessage(conversation);

			if (lastMessage) {
				messagePreview = lastMessage.text.payload.messagePayload.message;
				lastSenderId = lastMessage.senderId;
			} else {
				return undefined;
			}

			return {
				conversationId: conversation.conversationId,
				numberOfUnreadMessages: conversation.numberOfUnreadMessages,
				partners: conversation.partners,
				messagePreview,
				lastUpdate: conversation.lastUpdate,
				lastSenderId,
				partnersHash: conversation.partnersHash,
			};
		}).filter((conversation) => conversation);
	};

	sort = (): { time: Date; id: string }[] => {
		const timeAndId = Object.entries(this.dataMap).map(([, conversation]: ConversationTuple) => {
			return {
				time: conversation.lastUpdate as Date,
				id: conversation.conversationId as string,
			};
		});

		timeAndId.sort((conv1, conv2) => {
			if (conv1.time > conv2.time) {
				return -1;
			}

			if (conv2.time > conv1.time) {
				return 1;
			}

			return 0;
		});

		return timeAndId;
	};

	addMessage = (message: Message, increaseUnreadCount: boolean): void => {
		const conversation = this.dataMap[message.conversationId] as ChatConversation;

		conversation.addMessage(message, increaseUnreadCount);

		this.dataMap[message.conversationId] = conversation.getCopy();
		this.markUpdate();
	};

	addOlderMessages = (conversationId: string, messages: Message[]): void => {
		const conversation = this.dataMap[conversationId];

		conversation.addOlderMessages(messages);

		this.dataMap[conversationId] = conversation.getCopy();
		this.markUpdate();
	};

	addTyping = (conversationId: string, userId: string): void => {
		const conversation = this.dataMap[conversationId];
		conversation.typing = conversation.typing.concat(userId);

		this.dataMap[conversationId] = { ...conversation };
		this.markUpdate();
	};

	removeTyping = (conversationId: string, userId: string): void => {
		const conversation = this.dataMap[conversationId];
		conversation.typing = conversation.typing.filter((uuid: string) => {
			return uuid !== userId;
		});

		this.dataMap[conversationId] = { ...conversation };
		this.markUpdate();
	};

	addRead = ({
		conversationId,
		messageId,
		readerId,
	}: {
		conversationId: string;
		messageId: string;
		readerId: string;
	}): void => {
		const conversation = this.dataMap[conversationId];
		conversation.addRead(messageId, readerId);

		this.dataMap[conversationId] = { ...conversation };
		this.markUpdate();
	};

	deleteMessage = ({ conversationId, messageId }: { conversationId: string; messageId: string }): void => {
		const conversation = this.dataMap[conversationId];
		conversation.deleteMessage(messageId);

		this.dataMap[conversationId] = { ...conversation };
		this.markUpdate();
	};

	removeMessage = ({ conversationId, messageId }: { conversationId: string; messageId: string }): void => {
		const conversation = this.dataMap[conversationId];
		conversation.removeMessage(messageId);

		this.dataMap[conversationId] = { ...conversation };
		this.markUpdate();
	};

	getUnreadConversationsCount = (): number => {
		return Object.entries(this.dataMap)
			.map(([, conversation]: ConversationTuple): number => {
				if (conversation.numberOfUnreadMessages > 0) {
					return 1;
				}
				return 0;
			})
			.reduce((accumulator: number, currentValue: number) => {
				return accumulator + currentValue;
			}, 0);
	};
}
